import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../../hooks/redux';
import { SetNewPasswordForm } from '../../../../forms/SetNewPasswordFrom';
import { modalSlice } from '../../../../../store/reducers/ModalSlice';
import { internetApi } from '../../../../../services/apiServices/user/internet';
import { useCustomSnackbar } from '../../../../../hooks/useCustomEnqueueSnackbar';
import { ConnectionProtocolTypes } from '../../../../../models/Accounts';
import { getPpoeSettingsLink } from '../../../../../utils';
import { ModalVariants } from '../../../../../models/Common';

interface EditInternetPasswordProps {
  handleClose: () => void;
  accountId: string | number;
  connectionProtocolId: number;
  selectedLanguage: string;
}

export const EditInternetPassword: FC<EditInternetPasswordProps> = ({
  handleClose,
  accountId,
  connectionProtocolId,
  selectedLanguage,
}) => {
  const { t } = useTranslation();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [editInternetDevice, { isLoading }] = internetApi.useUpdateLoginOrPasswordMutation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;

  const handleEditPassword = async ({ password, currentPassword }: { password: string; currentPassword: string }) => {
    return editInternetDevice({ password, accountId, currentPassword })
      .unwrap()
      .then(() => {
        customEnqueueSnackbar(t('snackbar.internetDevicePasswordChanged'), 'success');
        handleClose();
      });
  };

  const handleForgotPassword = () => {
    dispatch(openModal({ modalVariants: ModalVariants.LogOut }));
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('modals.changePassword')}</article>
        {connectionProtocolId === ConnectionProtocolTypes.PPPoE && (
          <div className="modals__attention">
            {t('serviceManagement.afterChangingDataInternetConnectionWillBeInterrupted')}{' '}
            <a href={getPpoeSettingsLink(selectedLanguage)} rel="noreferrer" target="_blank">
              {t('serviceManagement.pppoeConnectionSettings')}
            </a>
          </div>
        )}
      </div>
      <SetNewPasswordForm
        submitNewPassword={handleEditPassword}
        buttonText={t('buttons.saveChanges')}
        description={false}
        helperText={t('input.numberOfCharactersInLatinNumbers_', { from: 6, to: 16 })}
        isLoading={isLoading}
        handleForgotPassword={handleForgotPassword}
      />
    </div>
  );
};
